import { saveAs } from 'file-saver';
import { get } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { exportUsersExcelFile } from '~/components/Admin/UsersPage/UsersPage.data';
import { trackSegment } from '~/components/SegmentAnalytics';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { DeactivateUserModal } from '~/components/SuperAdmin/DeactivateUserModal';
import { UserFormPanel } from '~/components/SuperAdmin/UserFormPanel';
import { ResetUserPasswordModal } from '~/components/Users/ResetUserPasswordModal';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { useDebouncedState } from '~/eds/src/hooks/useDebouncedState';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { TableContextType } from '~/enums/TableContextType';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTableSettings } from '~/hooks/useTableSettings';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import { UserAttributes } from '~/redux/api/methods/roles/roles';
import { ParsedType } from '~/redux/api/transformers/utils';
import { coerceRtkqError } from '~/redux/api/utils';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';
import { Nullable } from '~/types/Nullable';

import { getStatusLabels } from './utils';

const current = {
  text: 'Users',
  pathname: RoutePathType.AdminUsers,
};

const COLUMNS = [
  'name',
  'email',
  'role',
  'departments',
  'job_title',
  'status',
  'last_login',
];

const STATUSES = {
  Active: 'success',
  Deactivated: 'inactive',
  Pending: 'warning',
};

const Page = () => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const statusLabels = getStatusLabels(intl);
  const [debouncedState, search, setSearch] =
    useDebouncedState<Nullable<string>>('');

  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [deactivateVisible, setDeactivateVisible] = useState(false);
  const [resetPasswordVisible, setResetPassword] = useState(false);
  const { toast } = useToast();
  const [sendInvitationEmail] = api.endpoints.sendInvitationEmail.useMutation();
  const [resendReactivatedUserInvite] =
    api.endpoints.resendReactivatedUserInvite.useMutation();
  const isEmailNotificationEnabled = useFlag(
    FlagType.ReactivateUsersEmailNotification,
  );

  const isRolodexDataSourceEnabled = useFlag(FlagType.RolodexAsUserDataSource);
  const [reactivateUser, reactivateUserResult] =
    api.endpoints.reactivateUser.useMutation();
  const {
    isError: isErrorReactivateUser,
    isLoading: isLoadingReactivateUser,
    isSuccess: isSuccessReactivateUser,
  } = reactivateUserResult;
  const [cancelUserInvite, cancelUserInviteResult] =
    api.endpoints.cancelUserInvite.useMutation();
  const {
    isError: isErrorCancelUserInvite,
    isLoading: isLoadingCancelUserInvite,
    isSuccess: isSuccessCancelUserInvite,
  } = cancelUserInviteResult;

  const currentUser = useCurrentUser();
  const [selectedUser, setSelectedUser] =
    useState<Nullable<ParsedType<UserAttributes>>>(null);

  const nav = generateNav({
    current,
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId: currentUser.client,
    },
    intl,
  });

  const {
    tableSettings = {
      pageSize: 10,
      columnOrder: COLUMNS,
    },
  } = useTableSettings(TableContextType.Users);

  const [getUsers, { isFetching, data }] =
    api.endpoints.getUsers.useLazyQuery();
  const results = data?.results ?? [];

  const [pageSize, setPageSize] = useState(tableSettings.pageSize);

  const customAttributesColumns = useMemo(
    () =>
      (data?.columns ?? []).map((column) => ({
        key: column.key,
        title: column.label,
        width: 'm',
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          text: get(user.customAttributeValues, column.key, ''),
        }),
      })),
    [data?.columns],
  );

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: intl.formatMessage({
          id: 'SuperAdminUsers.userColumnTitle',
          defaultMessage: 'User',
          description: 'Column title for user information in the users table',
        }),
        width: 'l',
        cellType: 'user',
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          user: {
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
          },
          mode: 'avatar-name',
        }),
      },
      {
        key: 'email',
        title: intl.formatMessage({
          id: 'SuperAdminUsers.emailColumnTitle',
          defaultMessage: 'Email',
          description: 'Column title for email in the users table',
        }),
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          text: user.email,
        }),
      },
      {
        key: 'role',
        title: intl.formatMessage({
          id: 'SuperAdminUsers.roleColumnTitle',
          defaultMessage: 'Role',
          description: 'Column title for role in the users table',
        }),
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          text: user.role,
        }),
      },
      {
        key: 'departments',
        title: intl.formatMessage({
          id: 'SuperAdminUsers.departmentColumnTitle',
          defaultMessage: 'Department',
          description: 'Column title for department in the users table',
        }),
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          text: user.departments
            .map((department) => department.departmentName)
            .join(', '),
        }),
      },
      {
        key: 'job_title',
        title: intl.formatMessage({
          id: 'SuperAdminUsers.jobTitleColumnTitle',
          defaultMessage: 'Job Title',
          description: 'Column title for job title in the users table',
        }),
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          text: user.jobTitle,
        }),
      },
      {
        key: 'status',
        title: intl.formatMessage({
          id: 'SuperAdminUsers.statusColumnTitle',
          defaultMessage: 'Status',
          description: 'Column title for status in the users table',
        }),
        cellType: 'chips',
        disableSortBy: true,
        mapCellProps: (user: ParsedType<UserAttributes>) => ({
          chips: [
            { text: statusLabels[user.status], status: STATUSES[user.status] },
          ],
        }),
      },
      {
        key: 'last_login',
        title: intl.formatMessage({
          id: 'SuperAdminUsers.lastSignInColumnTitle',
          defaultMessage: 'Last sign in',
          description: 'Column title for last sign in date in the users table',
        }),
        width: 'm',
        disableSortBy: true,
        cellType: 'datetime',
        mapCellProps: ({ lastLogin }: ParsedType<UserAttributes>): Object => {
          return {
            datetime: lastLogin,
            format: 'short_datetime',
          };
        },
      },
      ...customAttributesColumns,
    ],
    [customAttributesColumns],
  );

  const columnGroups = useMemo(() => {
    return [
      {
        name: 'default',
        label: intl.formatMessage({
          id: 'SuperAdminUsers.defaultAttributesGroup',
          defaultMessage: 'Default Attributes',
          description: 'Label for the default attributes column group',
        }),
        columns: [
          'name',
          'email',
          'role',
          'departments',
          'job_title',
          'status',
          'last_login',
        ],
      },
      {
        name: 'custom',
        label: intl.formatMessage({
          id: 'SuperAdminUsers.customAttributesGroup',
          defaultMessage: 'Custom Attributes',
          description: 'Label for the custom attributes column group',
        }),
        columns: customAttributesColumns.map((column) => column.key),
      },
    ];
  }, [columns, customAttributesColumns]);

  const getRowActions = useCallback(() => {
    return [
      {
        label: intl.formatMessage({
          id: 'SuperAdminUserPage.deactivateUser',
          defaultMessage: 'Deactivate user',
          description:
            'Row action label to deactivate a user in the users table',
        }),
        onClick: async (user: ParsedType<UserAttributes>): Promise<void> => {
          setDeactivateVisible(true);
          setSelectedUser(user);
        },
        icon: '',
        condition: (user: ParsedType<UserAttributes>) =>
          user.status === 'Active',
      },
      {
        label: intl.formatMessage({
          id: 'SuperAdminUserPage.reactivateUser',
          defaultMessage: 'Reactivate user',
          description:
            'Row action label to reactivate a user in the users table',
        }),
        onClick: async (user: ParsedType<UserAttributes>) => {
          trackSegment('submitReactivateUser', {
            id: user.id,
            user_id: currentUser.id,
            client_id: currentUser.client,
          });
          reactivateUser({
            userId: user.id,
            enableRolodex: isRolodexDataSourceEnabled,
          });
          setSelectedUser(user);
        },
        icon: '',
        condition: (user: ParsedType<UserAttributes>) =>
          user.status === 'Deactivated',
      },
      {
        label: intl.formatMessage({
          id: 'SuperAdminUsers.resetPassword',
          defaultMessage: 'Reset password',
          description: 'Row action label to reset user password',
        }),
        onClick: async (user: ParsedType<UserAttributes>) => {
          setResetPassword(true);
          setSelectedUser(user);
        },
        icon: '',
        condition: (user: ParsedType<UserAttributes>) =>
          user.status === 'Active' && !user.ssoUser,
      },
      {
        label: intl.formatMessage({
          id: 'SuperAdminUsers.cancelInvite',
          defaultMessage: 'Cancel invite',
          description: 'Row action label to cancel user invite',
        }),
        onClick: (user: ParsedType<UserAttributes>) => {
          trackSegment('submitCancelUserInvite', {
            id: user.id,
            user_id: currentUser.id,
            client_id: currentUser.client,
          });
          cancelUserInvite({
            userId: user.id,
            enableRolodex: isRolodexDataSourceEnabled,
          });
          setSelectedUser(user);
        },
        icon: '',
        condition: (user: ParsedType<UserAttributes>) =>
          user.status === 'Pending',
      },
      {
        label: intl.formatMessage({
          id: 'SuperAdminUsers.sendInvite',
          defaultMessage: 'Send invite',
          description: 'Row action label to send user invite',
        }),
        onClick: async (user: ParsedType<UserAttributes>) => {
          trackSegment('submitCancelUserInvite', {
            id: user.id,
            user_id: currentUser.id,
            client_id: currentUser.client,
          });
          setIsLoading(true);
          try {
            const resendRequest = isEmailNotificationEnabled
              ? resendReactivatedUserInvite
              : sendInvitationEmail;
            await resendRequest({
              userId: user.id,
              enableRolodex: isRolodexDataSourceEnabled,
            }).unwrap();
            toast({
              title: intl.formatMessage({
                id: 'SuperAdminUsers.inviteSent',
                defaultMessage: 'Invite sent',
                description: 'Toast title when invite is sent successfully',
              }),
              message: intl.formatMessage(
                {
                  id: 'SuperAdminUsers.inviteSentMessage',
                  defaultMessage: 'User will receive an email at "{email}"',
                  description:
                    'Toast message when invite is sent successfully. Example: email = "john@example.com"',
                },
                { email: user.email },
              ),
              status: 'success',
            });
          } catch (e: any) {
            const resp = e?.response || {};
            const msg = resp.data?.errors?.[0];
            toast({
              message:
                msg?.title ||
                intl.formatMessage({
                  id: 'SuperAdminUserPage.resendInviteError',
                  defaultMessage:
                    'An error occurred while resending the user invite.',
                  description:
                    'Error message shown when resending a user invite fails',
                }),
              status: 'danger',
            });
          } finally {
            setIsLoading(false);
          }
        },
        icon: '',
        condition: (user: ParsedType<UserAttributes>) =>
          user.status === 'Pending',
      },
    ];
  }, [currentUser]);

  const onPaginate = useCallback(
    ({ pageIndex }: { pageIndex: number }) => {
      if (pageIndex !== page) {
        setPage(pageIndex);
      }
    },
    [page],
  );
  const onPageSizeChange = useCallback((pageSize: number) => {
    setPage(1);
    setPageSize(pageSize);
  }, []);

  const handleHidePanel = () => {
    setPanelVisible(false);
    setSelectedUser(null);
  };

  const handleHideDeactivate = () => {
    setDeactivateVisible(false);
    setSelectedUser(null);
  };

  const handleHideResetPassword = () => {
    setResetPassword(false);
    setSelectedUser(null);
  };

  const handleSearch = (search: Nullable<string>) => {
    setSearch(search);
  };

  const handleExportUsers = async () => {
    const timestamp = formatDate(new Date(), 'utc');
    const filename = `evisort-users-${timestamp}.xlsx`;
    setIsLoading(true);
    try {
      const response = await exportUsersExcelFile({
        clientId: currentUser.client,
        onlyHeaders: false,
      });
      toast({
        message: intl.formatMessage({
          id: 'SuperAdminUserPage.fileDownloadedSuccessfully',
          defaultMessage: 'File downloaded successfully.',
          description:
            'Success message shown when the user export file is downloaded successfully',
        }),
        status: 'success',
      });
      // Adding unknown due to lacking types in the functions library
      saveAs(response as unknown as Blob, filename);
    } catch {
      toast({
        message: intl.formatMessage({
          id: 'SuperAdminUserPage.errorExportingFile',
          defaultMessage: 'Error exporting file',
          description: 'Error message shown when exporting the user file fails',
        }),
        status: 'danger',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getUsers({
      searchValue: search ?? undefined,
      page,
      pageSize,
      enableRolodex: isRolodexDataSourceEnabled,
    });
  }, [page, pageSize]);

  useEffect(() => {
    if (isSuccessReactivateUser) {
      if (!isEmailNotificationEnabled) {
        sendInvitationEmail({
          userId: selectedUser!.id,
          enableRolodex: isRolodexDataSourceEnabled,
        });
      }
      toast({
        message: intl.formatMessage(
          {
            id: 'SuperAdminUserPage.userReactivated',
            defaultMessage: '“{firstName} {lastName}” reactivated.',
            description:
              'Toast message when a user is reactivated. Example: firstName = "Jane", lastName = "Doe"',
          },
          {
            firstName: selectedUser?.firstName,
            lastName: selectedUser?.lastName,
          },
        ),
        status: 'success',
      });
      setSelectedUser(null);
    }
  }, [isSuccessReactivateUser]);

  useEffect(() => {
    if (isSuccessCancelUserInvite) {
      toast({
        title: intl.formatMessage({
          id: 'SuperAdminUserPage.inviteCancelled',
          defaultMessage: 'Invite canceled',
          description: 'Toast title when user invite is canceled',
        }),
        message: intl.formatMessage({
          id: 'SuperAdminUserPage.inviteCancelledMessage',
          defaultMessage: "The user's activation link is no longer valid",
          description: 'Toast message when user invite is canceled',
        }),
        status: 'success',
      });
      setSelectedUser(null);
    }
  }, [isSuccessCancelUserInvite]);

  useEffect(() => {
    if (isErrorReactivateUser) {
      const resp = coerceRtkqError(reactivateUserResult.error)?.response || {};
      const msg = resp.data?.errors?.[0];
      toast({
        message:
          msg?.title ||
          intl.formatMessage({
            id: 'SuperAdminUserPage.reactivationError',
            defaultMessage: 'An error occurred while reactivating the user.',
            description: 'Error message shown when reactivating a user fails',
          }),
        status: 'danger',
      });
      setSelectedUser(null);
    }
  }, [isErrorReactivateUser]);

  useEffect(() => {
    if (isErrorCancelUserInvite) {
      const resp =
        coerceRtkqError(cancelUserInviteResult.error)?.response || {};
      const msg = resp.data?.errors?.[0];
      toast({
        message:
          msg?.title ||
          intl.formatMessage({
            id: 'SuperAdminUserPage.cancelingError',
            defaultMessage:
              'An error occurred while cancelling the user invite.',
            description:
              'Error message shown when cancelling a user invite fails',
          }),
        status: 'danger',
      });
      setSelectedUser(null);
    }
  }, [isErrorCancelUserInvite]);

  useEffect(() => {
    setPage(1);
    if (debouncedState && debouncedState.length > 2) {
      getUsers({
        searchValue: debouncedState,
        page: 1,
        pageSize,
        enableRolodex: isRolodexDataSourceEnabled,
      });
      trackSegment('searchTextUsers', {
        search,
        user_id: currentUser.id,
        client_id: currentUser.client,
      });
    } else if (!debouncedState) {
      getUsers({
        searchValue: undefined,
        page: 1,
        pageSize,
        enableRolodex: isRolodexDataSourceEnabled,
      });
    }
  }, [debouncedState]);

  return (
    <PageLayout
      nav={nav}
      title={intl.formatMessage({
        id: 'SuperAdminUsers.pageTitle',
        defaultMessage: 'Users',
        description: 'Page title for the users management page',
      })}
      description={intl.formatMessage({
        id: 'SuperAdminUserPage.createUsersOr',
        defaultMessage:
          'Create users or manage user information, roles, and permissions.',
        description:
          'Description for the users page, explaining available actions such as creating and managing users, roles, and permissions',
      })}
      actions={[
        {
          level: 'secondary',
          text: intl.formatMessage({
            id: 'SuperAdminUsers.exportUsers',
            defaultMessage: 'Export Users',
            description: 'Button label to export users',
          }),
          isLoading: isLoading,
          onClick: () => {
            trackSegment('exportUsers', {
              user_id: currentUser.id,
              client_id: currentUser.client,
            });
            handleExportUsers();
          },
        },
        {
          id: OnboardingIdType.AddNewUser,
          level: 'primary',
          text: intl.formatMessage({
            id: 'SuperAdminUserPage.newUser',
            defaultMessage: 'New User',
            description: 'Button label to open the new user creation panel',
          }),
          onClick: () => {
            setPanelVisible(true);
            trackSegment('openCreateUserPanel', {
              user_id: currentUser.id,
              client_id: currentUser.client,
            });
          },
        },
      ]}
    >
      <Layout justify="space-between">
        <Layout flex="1" mr={4}>
          <SearchInput
            width="100%"
            name="search"
            value={search}
            onChange={handleSearch}
            placeholder={intl.formatMessage({
              id: 'SuperAdminUsers.searchPlaceholder',
              defaultMessage: 'Search users',
              description: 'Placeholder text for the user search input',
            })}
          />
        </Layout>
      </Layout>
      <Layout direction="column" spacing={8} mt={6}>
        <PersistedTable
          name={TableContextType.Users}
          data={results}
          options={{
            enableSelectRows: false,
            enablePageSizeSelect: true,
          }}
          getRowActions={getRowActions}
          state={{
            pageIndex: page,
            pageSize,
            columnOrder: tableSettings.columnOrder,
          }}
          isLoading={
            isFetching ||
            isLoadingReactivateUser ||
            isLoadingCancelUserInvite ||
            isLoading
          }
          columns={columns}
          columnGroups={columnGroups}
          context={TableContextType.Users}
          totalCount={data?.total}
          onPaginate={onPaginate}
          onPageSizeChange={onPageSizeChange}
        />
        {data?.results?.length === 0 && search && (
          <Layout align="center" justify="center">
            <ContentContainer
              placeholderContent={{
                title: intl.formatMessage({
                  id: 'SuperAdminUsers.noUsersMatchedSearch',
                  defaultMessage: 'No Users Matched this Search',
                  description:
                    'Title shown when no users match the search criteria',
                }),
                description: intl.formatMessage({
                  id: 'SuperAdminUsers.searchSuggestion',
                  defaultMessage:
                    'Try another search, or use the filters next to the search bar to find a user by role, department, and more.',
                  description:
                    'Suggestion text shown when no users match the search',
                }),
              }}
            />
          </Layout>
        )}
      </Layout>
      <UserFormPanel visible={panelVisible} onHide={handleHidePanel} />
      <DeactivateUserModal
        isVisible={deactivateVisible}
        onHide={handleHideDeactivate}
        user={selectedUser}
      />
      <ResetUserPasswordModal
        visible={resetPasswordVisible}
        onHide={handleHideResetPassword}
        user={selectedUser}
      />
    </PageLayout>
  );
};

export default Page;
